import React, { useEffect, useMemo, useState } from "react";
import api, { apiError } from "@/lib/api";
import { brl, fmtDate, todayISO } from "@/lib/format";
import { PageHeader, EmptyState } from "@/components/Common";
import { Toolbar, TableShell, FormModal, TextField, TextArea, SelectField, MoneyField } from "@/components/FormKit";
import { useAuth } from "@/context/AuthContext";
import { Loader2, ArrowRight, RotateCcw, Link2, Search } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { from_account_id: "", to_account_id: "", amount: "", date: todayISO(), description: "", document_number: "" };

const STATUS = {
  efetivada: { label: "Efetivada", cls: "bg-emerald-50 text-emerald-700" },
  estornada: { label: "Estornada", cls: "bg-rose-50 text-rose-600" },
  reclassificada_historica: { label: "Reclassificada", cls: "bg-amber-50 text-amber-700" },
};

export default function TransfersPage() {
  const { hasPermission } = useAuth();
  const [rows, setRows] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [tab, setTab] = useState("lista");
  const [cands, setCands] = useState(null);
  const [loadingCands, setLoadingCands] = useState(false);

  const load = () => { setLoading(true); api.get("/transfers").then((r) => setRows(r.data)).finally(() => setLoading(false)); };
  useEffect(() => { load(); api.get("/accounts").then((r) => setAccounts(r.data.filter((a) => a.status === "ativa"))).catch(() => {}); }, []);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const accOpts = useMemo(() => accounts.map((a) => ({ value: a.id, label: a.name })), [accounts]);

  const submit = async (e) => {
    e.preventDefault();
    if (form.from_account_id === form.to_account_id) { toast.error("Origem e destino devem ser diferentes."); return; }
    if (!(parseFloat(form.amount) > 0)) { toast.error("Informe um valor maior que zero."); return; }
    setSubmitting(true);
    try {
      await api.post("/transfers", { ...form, amount: parseFloat(form.amount) || 0 });
      toast.success("Transferência registrada."); setOpen(false); load();
    } catch (err) { toast.error(apiError(err)); } finally { setSubmitting(false); }
  };

  const cancel = async (row) => {
    const reason = window.prompt("Motivo do estorno desta transferência:");
    if (reason === null) return;
    if (!reason.trim()) { toast.error("O motivo é obrigatório."); return; }
    try { await api.post(`/transfers/${row.id}/cancel`, { reason }); toast.success("Transferência estornada."); load(); }
    catch (err) { toast.error(apiError(err)); }
  };

  const loadCands = () => {
    setLoadingCands(true);
    api.get("/transfers/historical-candidates?days=3").then((r) => setCands(r.data)).catch((err) => toast.error(apiError(err))).finally(() => setLoadingCands(false));
  };

  const link = async (c) => {
    if (!window.confirm(`Vincular como transferência interna?\n\n${c.out_account} → ${c.in_account}\nValor: ${brl(c.amount)}\n\nOs lançamentos serão reclassificados (deixam de contar como receita/despesa). Os registros originais são preservados.`)) return;
    try { await api.post("/transfers/link-historical", { out_tx_id: c.out_tx_id, in_tx_id: c.in_tx_id }); toast.success("Lançamentos vinculados como transferência."); loadCands(); load(); }
    catch (err) { toast.error(apiError(err)); }
  };

  return (
    <div className="space-y-6">
      <PageHeader title="Transferências entre Contas" subtitle="Movem saldo entre contas do clube — não contam como receita nem despesa." />

      <div className="flex gap-2 border-b border-slate-200">
        <button onClick={() => setTab("lista")} data-testid="transfers-tab-lista" className={`px-4 py-2 text-sm font-medium -mb-px border-b-2 ${tab === "lista" ? "border-brand text-brand" : "border-transparent text-slate-500"}`}>Transferências</button>
        {hasPermission("financial_transfer.reclassify_historical") && (
          <button onClick={() => setTab("historicas")} data-testid="transfers-tab-historicas" className={`px-4 py-2 text-sm font-medium -mb-px border-b-2 ${tab === "historicas" ? "border-brand text-brand" : "border-transparent text-slate-500"}`}>Possíveis transferências históricas</button>
        )}
      </div>

      {tab === "lista" ? (
        <>
          {hasPermission("financial_transfer.create") && (
            <Toolbar onNew={() => { setForm(EMPTY); setOpen(true); }} newLabel="Nova Transferência" newTestId="add-transfer-button" />
          )}
          {loading ? <div className="flex justify-center py-16"><Loader2 className="h-7 w-7 animate-spin text-brand" /></div>
            : rows.length === 0 ? <EmptyState text="Nenhuma transferência registrada." />
            : (
            <TableShell testId="transfers-table" headers={[{ label: "Data" }, { label: "Origem → Destino" }, { label: "Descrição" }, { label: "Valor", align: "right" }, { label: "Status" }, { label: "Ações", align: "right" }]}>
              {rows.map((r) => (
                <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50/60" data-testid={`transfer-row-${r.id}`}>
                  <td className="px-4 py-3.5 text-slate-500">{fmtDate(r.date)}</td>
                  <td className="px-4 py-3.5 font-medium text-slate-800"><span className="inline-flex items-center gap-1.5">{r.from_account_name} <ArrowRight className="h-3.5 w-3.5 text-brand" /> {r.to_account_name}</span></td>
                  <td className="px-4 py-3.5 text-slate-500">{r.description || "—"}</td>
                  <td className="px-4 py-3.5 text-right font-mono font-semibold text-slate-900">{brl(r.amount)}</td>
                  <td className="px-4 py-3.5"><span className={`text-xs font-medium px-2 py-1 rounded-full ${STATUS[r.status]?.cls || "bg-slate-100 text-slate-600"}`}>{STATUS[r.status]?.label || r.status}</span></td>
                  <td className="px-4 py-3.5 text-right">
                    {r.status === "efetivada" && hasPermission("financial_transfer.cancel") && (
                      <button onClick={() => cancel(r)} data-testid={`transfer-cancel-${r.id}`} className="p-1.5 rounded-md hover:bg-rose-50 text-rose-500" title="Estornar transferência"><RotateCcw className="h-4 w-4" /></button>
                    )}
                  </td>
                </tr>
              ))}
            </TableShell>
          )}
        </>
      ) : (
        <div className="space-y-4">
          <div className="flex items-center justify-between gap-3">
            <p className="text-sm text-slate-500">O sistema apenas <strong>sugere</strong> pares (uma saída + uma entrada, mesmo valor, contas diferentes, datas próximas). Nada é alterado automaticamente.</p>
            <button onClick={loadCands} data-testid="transfers-find-candidates" className="bg-brand hover:bg-brand-hover text-white font-medium px-4 py-2 rounded-lg text-sm flex items-center gap-2 shrink-0">
              {loadingCands ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />} Buscar candidatos
            </button>
          </div>
          {cands === null ? <EmptyState text="Clique em Buscar candidatos para analisar o histórico." />
            : cands.candidates.length === 0 ? <EmptyState text="Nenhum par candidato encontrado." />
            : (
            <TableShell testId="candidates-table" headers={[{ label: "Origem (saída)" }, { label: "Destino (entrada)" }, { label: "Datas" }, { label: "Valor", align: "right" }, { label: "Ação", align: "right" }]}>
              {cands.candidates.map((c, i) => (
                <tr key={i} className="border-b border-slate-100" data-testid={`candidate-row-${i}`}>
                  <td className="px-4 py-3 text-slate-700">{c.out_account}<div className="text-xs text-slate-400">{c.out_description}</div></td>
                  <td className="px-4 py-3 text-slate-700">{c.in_account}<div className="text-xs text-slate-400">{c.in_description}</div></td>
                  <td className="px-4 py-3 text-slate-500 text-xs">{fmtDate(c.out_date)} / {fmtDate(c.in_date)}{c.day_diff ? ` (${c.day_diff}d)` : ""}</td>
                  <td className="px-4 py-3 text-right font-mono font-semibold">{brl(c.amount)}</td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => link(c)} data-testid={`candidate-link-${i}`} className="inline-flex items-center gap-1 text-sm text-brand hover:underline"><Link2 className="h-4 w-4" /> Vincular</button>
                  </td>
                </tr>
              ))}
            </TableShell>
          )}
        </div>
      )}

      <FormModal open={open} onOpenChange={setOpen} title="Nova Transferência entre Contas" onSubmit={submit} submitting={submitting} testId="transfer-form">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <SelectField label="Conta de Origem" value={form.from_account_id} onChange={(v) => set("from_account_id", v)} testId="transfer-from" options={accOpts} required />
          <SelectField label="Conta de Destino" value={form.to_account_id} onChange={(v) => set("to_account_id", v)} testId="transfer-to" options={accOpts} required />
          <MoneyField label="Valor" value={form.amount} onChange={(v) => set("amount", v)} testId="transfer-amount" required />
          <TextField label="Data" type="date" value={form.date} onChange={(v) => set("date", v)} testId="transfer-date" required />
          <TextField label="Documento (opcional)" value={form.document_number} onChange={(v) => set("document_number", v)} testId="transfer-document" />
          <div />
          <TextArea label="Descrição / Histórico" value={form.description} onChange={(v) => set("description", v)} testId="transfer-description" className="sm:col-span-2" />
        </div>
        <p className="text-xs text-slate-400 mt-2">A transferência move o saldo entre as contas e <strong>não</strong> é contabilizada como receita ou despesa. Tarifas bancárias devem ser lançadas separadamente como despesa.</p>
      </FormModal>
    </div>
  );
}
